import React from "react";
import { Star, StarHalf } from "lucide-react";

/** Read-only stars: pass `value` (0..5, can be fractional) and `count` (optional). */
export function StarRating({ value = 0, count = null, size = 14, className = "", showValue = false, dataTestId }) {
    const safe = Math.max(0, Math.min(5, Number(value) || 0));
    const full = Math.floor(safe);
    const half = safe - full >= 0.25 && safe - full < 0.75;
    const ceil = safe - full >= 0.75 ? full + 1 : full;
    return (
        <span className={`inline-flex items-center gap-1 ${className}`} data-testid={dataTestId}>
            <span className="inline-flex">
                {[0, 1, 2, 3, 4].map((i) => {
                    if (i < ceil) {
                        if (i === full && half) return <StarHalf key={i} className="text-amber-500 fill-amber-500" style={{ width: size, height: size }} />;
                        return <Star key={i} className="text-amber-500 fill-amber-500" style={{ width: size, height: size }} />;
                    }
                    return <Star key={i} className="text-stone-300" style={{ width: size, height: size }} />;
                })}
            </span>
            {showValue && safe > 0 && (
                <span className="text-xs font-semibold text-stone-700">{safe.toFixed(1)}</span>
            )}
            {count != null && (
                <span className="text-xs text-stone-500">({count})</span>
            )}
        </span>
    );
}

/** Clickable star input — `value` (1..5) + `onChange(n)`. */
export function StarInput({ value = 0, onChange, size = 26, dataTestIdBase = "star" }) {
    const [hover, setHover] = React.useState(0);
    const shown = hover || value;
    return (
        <div className="inline-flex items-center gap-1" role="radiogroup">
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    type="button"
                    key={n}
                    onMouseEnter={() => setHover(n)}
                    onMouseLeave={() => setHover(0)}
                    onClick={() => onChange?.(n)}
                    aria-label={`Rate ${n} star${n === 1 ? "" : "s"}`}
                    aria-checked={value === n}
                    role="radio"
                    className="cursor-pointer p-0.5 transition-transform hover:scale-110"
                    data-testid={`${dataTestIdBase}-${n}`}
                >
                    <Star
                        className={n <= shown ? "text-amber-500 fill-amber-500" : "text-stone-300"}
                        style={{ width: size, height: size }}
                    />
                </button>
            ))}
            <span className="ml-2 text-sm text-stone-600 min-w-[60px]">{shown ? `${shown}/5` : "Tap a star"}</span>
        </div>
    );
}
